import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum} from '@datadog/browser-rum';
import getConfig from 'next/config';

export function initDatadog() {
  // Next.js executes top-level code at build time. See https://github.com/vercel/next.js/discussions/16840
  // So use NEXT_PHASE to avoid Datadog.init being executed during the build phase
  if (process.env.NEXT_PHASE === 'phase-production-build') {
    return;
  }

  // 開発環境ではDatadogに送信しない
  if ((process.env.NODE_ENV as string) === 'development') {
    return;
  }

  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const site = process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com';
  const releaseStage = process.env.NEXT_PUBLIC_RELEASE_STAGE || 'development';

  // Datadog用の環境名にマッピング
  const datadogEnvMap: Record<string, string> = {
    production: 'prd',
    staging: 'stg',
    development: 'dev',
    tongue: 'tongue',
    chuckflap: 'chuckflap',
    sirloin: 'sirloin',
    'proto-tmp': 'proto-tmp',
  };
  const datadogEnv = datadogEnvMap[releaseStage] ?? releaseStage;

  // 環境変数が設定されていない場合は警告を出して終了
  if (!clientToken || !applicationId) {
    console.warn(
      '[Datadog] clientToken or applicationId is not set. Skipping initialization.',
    );
    return;
  }

  const {publicRuntimeConfig} = getConfig() || {};
  const version = publicRuntimeConfig?.version;

  // RUM初期化
  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service: 'gsskt-frontend',
    env: datadogEnv,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
  });

  // Browser Logs初期化
  datadogLogs.init({
    clientToken,
    site,
    service: 'gsskt-frontend',
    env: datadogEnv,
    version,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
  });
}

/**
 * ログインユーザー情報をDatadogに設定する
 * ログイン後に呼び出すことで、ユーザーの行動を追跡できる
 */
export function setDatadogUser(user: {id: string}) {
  if (typeof window === 'undefined') {
    return;
  }
  if ((process.env.NODE_ENV as string) === 'development') {
    return;
  }
  datadogRum.setUser({id: user.id});
}

/**
 * ユーザー情報をクリアする
 * ログアウト時に呼び出す
 */
export function clearDatadogUser() {
  if (typeof window === 'undefined') {
    return;
  }
  if ((process.env.NODE_ENV as string) === 'development') {
    return;
  }
  datadogRum.clearUser();
}

/**
 * リクエストオブジェクトから機密情報を除外してサニタイズする
 */
function sanitizeRequest(
  request: unknown,
): Record<string, unknown> | undefined {
  if (!request || typeof request !== 'object') {
    return undefined;
  }

  const req = request as Record<string, unknown>;

  // 安全なフィールドのみを抽出
  return {
    method: req.method,
    url: req.url,
    statusCode: req.statusCode,
    // ヘッダーからは機密情報を除外
    headers: sanitizeHeaders(req.headers),
  };
}

/**
 * ヘッダーから機密情報を除外
 */
function sanitizeHeaders(
  headers: unknown,
): Record<string, unknown> | undefined {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }

  const h = headers as Record<string, unknown>;
  const sensitiveHeaders = [
    'cookie',
    'authorization',
    'x-api-key',
    'x-auth-token',
    'set-cookie',
  ];

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(h)) {
    if (!sensitiveHeaders.includes(key.toLowerCase())) {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * エラーをDatadogに通知する
 * Bugsnag.notify()の代替
 */
export function notifyError(
  error: Error,
  context?: {
    request?: unknown;
    severity?: 'error' | 'warn' | 'info';
  },
) {
  // サーバーサイドでは実行しない（ブラウザSDKはサーバーで動作しないため）
  if (typeof window === 'undefined') {
    return;
  }

  // 開発環境ではDatadogに送信しない
  if ((process.env.NODE_ENV as string) === 'development') {
    console.error('[Datadog] Error (not sent in development):', error);
    return;
  }

  const severity = context?.severity || 'error';
  const sanitizedRequest = sanitizeRequest(context?.request);

  // RUMにエラーを送信
  datadogRum.addError(error, {
    request: sanitizedRequest,
  });

  // Logsにもエラーを送信
  datadogLogs.logger[severity](error.message, {
    error: {
      kind: error.name,
      message: error.message,
      stack: error.stack,
    },
    request: sanitizedRequest,
  });
}
